import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { makeForm } from "@/lib/upload";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { EuroInput } from "@/components/EuroInput";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Handshake, Loader2, UploadCloud, FileCheck2, Plus, X } from "lucide-react";
import { emptySeller } from "@/components/PartyRows";
import { fmtCurrency } from "@/lib/format";
import { withCreditConfirm } from "@/components/DatesDialog";

const today = () => new Date().toISOString().slice(0, 10);
const num = (v) => (v === "" || v == null ? null : Number(v));

const Choice = ({ value, onChange, options, testid }) => (
  <div className="grid grid-cols-2 gap-2" data-testid={testid}>
    {options.map(([v, l]) => (
      <button key={String(v)} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${String(v)}`}
        className={`h-11 rounded-lg text-[15px] font-semibold border transition-colors ${value === v ? "bg-ec-green text-white border-ec-green" : "bg-white navy-text border-slate-300 hover:bg-slate-50"}`}>{l}</button>
    ))}
  </div>
);

function BuyerFields({ b, i, set, onRemove }) {
  const up = (k) => (e) => set(i, { ...b, [k]: e.target.value });
  return (
    <div className="space-y-2" data-testid={`pa-buyer-${i}`}>
      <div className="flex items-center justify-between"><Label>{i ? "Segundo comprador" : "Comprador"}</Label>
        {onRemove && <button type="button" onClick={onRemove} className="text-slate-400 hover:text-slate-600" aria-label="Remover" data-testid={`pa-buyer-${i}-remove`}><X size={16} /></button>}</div>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input value={b.name} onChange={up("name")} placeholder="Nome completo" data-testid={`pa-buyer-${i}-name`} />
        <Input value={b.phone || ""} onChange={up("phone")} inputMode="tel" placeholder="Telemóvel" data-testid={`pa-buyer-${i}-phone`} />
      </div>
      <Input value={b.email || ""} onChange={up("email")} type="email" placeholder="Email" data-testid={`pa-buyer-${i}-email`} />
    </div>
  );
}

export const PropostaAceite = ({ pid, p, askingPrice, onDone, preload, onPreloadUsed, label, className }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [read, setRead] = useState(null);
  const [reviewed, setReviewed] = useState(false);
  const [buyers, setBuyers] = useState([emptySeller()]);
  const [f, setF] = useState({ price: askingPrice || "", deposit: "", cpcv_deadline: "", cpcv_date: "", deed_date: "", has_credit: null, credit_bank: "", other: false, share_agency: "", share_pct: "50", by: "eu", colega: "" });
  const [colegas, setColegas] = useState([]);
  useEffect(() => { if (open) { api.get("/users").then(({ data }) => setColegas(data.filter((u) => u.role === "consultor" && u.id !== p?.consultant_id))).catch(() => {}); } }, [open, p?.consultant_id]);
  const fileRef = useRef();
  const set = (k, v) => setF((o) => ({ ...o, [k]: v }));
  const apply = (data, name) => {
    setRead({ ...data, name }); setReviewed(false);
    const s = data.suggested || {};
    setF((o) => ({ ...o, ...(s.price ? { price: s.price } : {}), ...(s.deposit ? { deposit: s.deposit } : {}),
      ...(s.deed_date ? { deed_date: s.deed_date } : {}), ...(s.has_credit ? { has_credit: true } : {}), ...(s.cpcv_date ? { cpcv_date: s.cpcv_date } : {}) }));
    if (s.buyers?.length) setBuyers(s.buyers.slice(0, 2).map((n) => ({ ...emptySeller(), name: n })));
  };
  useEffect(() => {
    if (preload) { apply({ ...preload, read: true }, preload.filename); setOpen(true); onPreloadUsed?.(); }
  }, [preload]); // eslint-disable-line react-hooks/exhaustive-deps

  const readFile = async (file) => {
    if (!file) return;
    setReading(true);
    try {
      const fd = await makeForm([["file", file]]);
      const { data } = await api.post("/angariacoes/read-proposal", fd);
      apply(data, file.name);
      toast[data.read ? "success" : "warning"](data.read ? "Documento lido. Reveja os dados antes de guardar." : "Não consegui ler o documento. Preencha à mão.");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setReading(false); }
  };

  const m = p?.mediation || {};
  const pct = m.commission_type === "pct" ? Number(m.commission_value) : null;
  const gross = m.commission_type === "valor" ? Number(m.commission_value) : pct && num(f.price) ? (num(f.price) * pct) / 100 : null;
  const commission = gross == null ? null : f.other && f.share_agency.trim() ? gross * (1 - (num(f.share_pct) || 0) / 100) : gross;
  const first = (buyers[0]?.name || "").trim().split(/\s+/)[0];

  const submit = async () => {
    if (read?.read && !reviewed) { toast.error("Confirme que reviu os dados lidos."); return; }
    if (!buyers[0]?.name?.trim() || !f.price) { toast.error("Indique o comprador e o preço acordado."); return; }
    if (f.has_credit == null) { toast.error("Indique se o comprador vai ter crédito."); return; }
    if (f.other && !f.share_agency.trim()) { toast.error("Indique a agência do comprador."); return; }
    if (f.by === "colega" && !f.colega) { toast.error("Escolha o consultor que trouxe o comprador."); return; }
    setBusy(true);
    try {
      const r = await withCreditConfirm((ok) => api.post(`/processes/${pid}/convert`, {
        confirm_credit: ok,
        price: num(f.price), deposit: num(f.deposit), proposal_date: today(), cpcv_deadline: f.cpcv_deadline || null,
        cpcv_date: f.cpcv_date || null, deed_date: f.deed_date || null, has_credit: f.has_credit, credit_bank: f.credit_bank || null,
        share_agency: f.other ? f.share_agency.trim() : null, share_pct: f.other ? num(f.share_pct) : null,
        consultor_comprador_id: f.by === "colega" ? f.colega : null,
        buyers: buyers.filter((b) => b.name?.trim()).map(({ _read, ...b }) => b), file_id: read?.file_id || null,
      }));
      if (!r) return;
      const { data } = r;
      toast.success(data.gestor ? `Proposta aceite registada. O processo passou para a gestão processual · ${data.gestor}`
        : data.sem_gestor ? "Proposta aceite registada. O processo passou para a gestão processual · Sem gestor (a Direção foi avisada)"
        : "Proposta aceite registada: o processo passou à fase CPCV.");
      if (data.renew?.length) toast.warning(`A renovar antes da escritura: ${data.renew.join(", ")}`, { duration: 8000 });
      setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setBuyer = (i, b) => setBuyers((l) => l.map((x, j) => (j === i ? b : x)));
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="proposta-aceite-btn" className={className}>{label || <><Handshake size={15} /> Registar proposta aceite</>}</button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="proposta-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl navy-text">Proposta aceite</DialogTitle>
            <DialogDescription>{[p?.card?.address, "passa para a gestão processual"].filter(Boolean).join(" · ")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <BuyerFields b={buyers[0]} i={0} set={setBuyer} />
            {buyers[1] ? <BuyerFields b={buyers[1]} i={1} set={setBuyer} onRemove={() => setBuyers((l) => l.slice(0, 1))} />
              : <button type="button" onClick={() => setBuyers((l) => [...l, emptySeller()])} className="inline-flex items-center gap-1 text-[15px] font-semibold navy-text hover:underline" data-testid="pa-add-buyer"><Plus size={15} /> Segundo comprador</button>}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Preço acordado (€)</Label><EuroInput value={f.price} onChange={(v) => set("price", v)} className="mt-1.5" data-testid="pa-price" /></div>
              <div><Label>Sinal (€)</Label><EuroInput value={f.deposit} onChange={(v) => set("deposit", v)} className="mt-1.5" data-testid="pa-deposit" /></div>
            </div>
            <div><Label>Data limite do CPCV</Label><DateInput value={f.cpcv_deadline} onChange={(e) => set("cpcv_deadline", e.target.value)} className="mt-1.5" data-testid="pa-cpcv" /></div>
            <div className="space-y-1.5"><Label>Financiamento</Label>
              <Choice value={f.has_credit} onChange={(v) => set("has_credit", v)} options={[[true, "Com crédito aprovado"], [false, "Sem crédito"]]} testid="pa-credit" /></div>
            <div className="space-y-1.5"><Label>Quem trouxe o comprador?</Label>
              <Choice value={f.by} onChange={(v) => setF((x) => ({ ...x, by: v, other: v === "agencia" }))} options={[["eu", "Eu (o angariador)"], ["colega", "Outro consultor da agência"], ["agencia", "Outra agência"]]} testid="pa-by" />
              {f.by === "colega" && (
                <select value={f.colega} onChange={(e) => set("colega", e.target.value)} className="mt-1 w-full h-11 rounded-md border border-slate-200 px-3 text-[15px] bg-white" data-testid="pa-colega">
                  <option value="">Escolher consultor…</option>
                  {colegas.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
              {f.other && (
                <div className="grid grid-cols-[1fr_120px] gap-2 pt-1">
                  <Input value={f.share_agency} onChange={(e) => set("share_agency", e.target.value)} placeholder="Nome da agência" data-testid="pa-share-agency" />
                  <div className="relative"><Input type="number" value={f.share_pct} onChange={(e) => set("share_pct", e.target.value)} className="pr-7" data-testid="pa-share-pct" /><span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">%</span></div>
                </div>
              )}
            </div>
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="proposta-upload" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
              <input ref={fileRef} type="file" accept=".pdf,image/*" hidden onChange={(e) => readFile(e.target.files?.[0])} />
              {reading ? <Loader2 size={15} className="animate-spin" /> : read ? <FileCheck2 size={15} /> : <UploadCloud size={15} />}{reading ? "A ler…" : read?.name || "Tem a proposta em PDF? Carregar para preencher (opcional)"}
            </button>
            {read?.read && <label className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer" data-testid="pa-reviewed"><Checkbox checked={reviewed} onCheckedChange={(v) => setReviewed(!!v)} /> Revi os dados lidos do documento</label>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} disabled={busy} className="h-11 px-5 rounded-lg border border-slate-300 bg-white font-semibold navy-text hover:bg-slate-50" data-testid="pa-cancel">Cancelar</button>
              <button onClick={submit} disabled={busy} data-testid="pa-submit" className="h-11 px-5 rounded-lg bg-ec-green text-white font-semibold inline-flex items-center gap-2 hover:bg-ec-greenDark transition-colors disabled:opacity-60">
                {busy && <Loader2 size={15} className="animate-spin" />} Guardar proposta
              </button>
            </div>
            <div className="rounded-lg bg-emerald-50 text-emerald-900 text-sm px-4 py-3" data-testid="pa-after">
              <b>Depois de guardar:</b> passa para a gestão processual · {first || "o comprador"} aparece nas Partes{commission != null ? ` · comissão recalculada (${fmtCurrency(commission)})` : ""} · Próximo passo: «Enviar o link para os dados do CPCV» (pela gestão processual, se a agência tiver) · fica no Histórico.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
